import { useState, useEffect } from 'react'
import { Plus, CheckCircle2, Circle, Clock, X, ChevronDown, ChevronRight, PlusCircle, XCircle, Edit3, RotateCcw, ArrowRightCircle } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { usePresalesDashboardStore } from '../store'
import { formatDate } from '../utils/dealHelpers'
import { useTeamMembers, useKeyPersonOptions, useTeamMemberOptions } from '../utils/taskHelpers'
import { TaskCreateForm } from './TaskCreateForm'
import { TaskEditForm } from './TaskEditForm'
import { KanbanColumn } from './KanbanBoard'
import type { PresalesDeal, PresalesTask, PresalesTaskStatus, PresalesTaskAuditEntry } from '@/api/types'

interface TasksSectionProps {
  deal: PresalesDeal
}

export function TasksSection({ deal }: TasksSectionProps) {
  const { tasks, isLoadingTasks, loadTasks, createTask, updateTask, toggleTaskStatus, deleteTask, taskAuditLog, isLoadingAuditLog, loadTaskAuditLog } = usePresalesDashboardStore()
  const [showForm, setShowForm] = useState(false)
  const [showAuditLog, setShowAuditLog] = useState(false)
  const [auditLogLoaded, setAuditLogLoaded] = useState(false)
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null)
  const [showHandover, setShowHandover] = useState(false)

  // Shared hooks for edit form options
  const teamMembers = useTeamMembers()
  const keyPersonOptions = useKeyPersonOptions(teamMembers)
  const teamMemberOptions = useTeamMemberOptions(teamMembers)

  useEffect(() => {
    if (deal.id) {
      loadTasks(deal.id)
    }
  }, [deal.id, loadTasks])

  const todoTasks = tasks.filter((t) => t.status === 'open')
  const waitingTasks = tasks.filter((t) => t.status === 'waiting')
  const handoverTasks = tasks.filter((t) => t.status === 'handover')
  const doneTasks = tasks.filter((t) => t.status === 'completed')
  const openTaskCount = todoTasks.length + waitingTasks.length

  const handleToggleForm = () => {
    setShowForm(!showForm)
  }

  const handleCreateSubmit = async (dealId: number | null, data: any) => {
    await createTask(dealId, data)
    setShowForm(false)
  }

  const handleStatusChange = (_task: PresalesTask, newStatus: PresalesTaskStatus) => {
    toggleTaskStatus(_task.id, newStatus)
  }

  const handleDelete = (task: PresalesTask) => {
    if (window.confirm('Delete this task?')) {
      deleteTask(task.id)
    }
  }

  const handleStartEdit = (task: PresalesTask) => {
    setEditingTaskId(task.id)
  }

  const handleSaveEdit = async (taskId: number, data: any) => {
    await updateTask(taskId, data)
    setEditingTaskId(null)
  }

  const handleCancelEdit = () => {
    setEditingTaskId(null)
  }

  const handleDrop = (taskId: number, newStatus: PresalesTaskStatus) => {
    const task = tasks.find((t) => t.id === taskId)
    if (task && task.status !== newStatus) {
      handleStatusChange(task, newStatus)
    }
  }

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide">Tasks</h2>
          {openTaskCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-900/30 text-amber-300 border border-amber-700/40">
              {openTaskCount} open
            </span>
          )}
          {tasks.length > 0 && openTaskCount === 0 && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-900/30 text-green-300 border border-green-700/40">
              All done
            </span>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={handleToggleForm}>
          {showForm ? (
            <>
              <X className="h-4 w-4 mr-1" />
              Cancel
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-1" />
              Add Task
            </>
          )}
        </Button>
      </div>

      {/* Inline Add Task Form */}
      {showForm && (
        <div className="mb-4">
          <TaskCreateForm
            deal={deal}
            defaultAssignedTo={deal.presales_engineer || ''}
            onSubmit={handleCreateSubmit}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {/* Kanban Board */}
      {isLoadingTasks ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      ) : tasks.length === 0 && !showForm ? (
        <div className="text-center py-8 text-sm text-[var(--text-muted)]">
          No tasks yet. Add a task to get started.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4">
            <KanbanColumn
              title="To Do"
              count={todoTasks.length}
              colorClass="text-blue-400 border-blue-500/40 bg-blue-900/20"
              icon={<Circle className="h-4 w-4" />}
              status="open"
              onDrop={handleDrop}
              tasks={todoTasks}
              onDelete={handleDelete}
              onEdit={handleStartEdit}
            />
            <KanbanColumn
              title="Waiting"
              count={waitingTasks.length}
              colorClass="text-amber-400 border-amber-500/40 bg-amber-900/20"
              icon={<Clock className="h-4 w-4" />}
              status="waiting"
              onDrop={handleDrop}
              tasks={waitingTasks}
              onDelete={handleDelete}
              onEdit={handleStartEdit}
            />
            <KanbanColumn
              title="Done"
              count={doneTasks.length}
              colorClass="text-green-400 border-green-500/40 bg-green-900/20"
              icon={<CheckCircle2 className="h-4 w-4" />}
              status="completed"
              onDrop={handleDrop}
              tasks={doneTasks}
              onDelete={handleDelete}
              onEdit={handleStartEdit}
              collapsible
            />
          </div>

          {/* Handover toggle */}
          {(handoverTasks.length > 0 || showHandover) && (
            <div className="mt-4">
              <button
                onClick={() => setShowHandover(!showHandover)}
                className="flex items-center gap-1.5 text-xs text-purple-400/80 hover:text-purple-300 transition-colors"
              >
                {showHandover ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                <ArrowRightCircle className="h-3.5 w-3.5" />
                Handed over to Implementations ({handoverTasks.length})
              </button>

              {showHandover && (
                <div className="mt-2">
                  <KanbanColumn
                    title="Handover"
                    count={handoverTasks.length}
                    colorClass="text-purple-400 border-purple-500/40 bg-purple-900/20"
                    icon={<ArrowRightCircle className="h-4 w-4" />}
                    status="handover"
                    onDrop={handleDrop}
                    tasks={handoverTasks}
                    onDelete={handleDelete}
                    onEdit={handleStartEdit}
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Edit Task Modal */}
      {editingTaskId && (() => {
        const editingTask = tasks.find((t) => t.id === editingTaskId)
        if (!editingTask) return null
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={handleCancelEdit}>
            <div className="absolute inset-0 bg-black/60" />
            <div className="relative z-10 w-full max-w-5xl mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl shadow-2xl">
                <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-color)]">
                  <h3 className="text-sm font-semibold text-[var(--text-primary)]">Edit Task</h3>
                  <button onClick={handleCancelEdit} className="p-1 rounded hover:bg-[var(--bg-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="p-5">
                  <TaskEditForm
                    task={editingTask}
                    teamMemberOptions={teamMemberOptions}
                    keyPersonOptions={keyPersonOptions}
                    onSave={handleSaveEdit}
                    onCancel={handleCancelEdit}
                  />
                </div>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Activity Log Toggle */}
      {tasks.length > 0 && (
        <div className="mt-4 pt-3 border-t border-[var(--border-color)]">
          <button
            onClick={() => {
              const newShowState = !showAuditLog
              setShowAuditLog(newShowState)
              if (newShowState && !auditLogLoaded) {
                loadTaskAuditLog(deal.id)
                setAuditLogLoaded(true)
              }
            }}
            className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
          >
            {showAuditLog ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {showAuditLog ? 'Hide activity log' : 'View activity log'}
          </button>

          {showAuditLog && (
            <div className="mt-3">
              {isLoadingAuditLog ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                </div>
              ) : taskAuditLog.length === 0 ? (
                <p className="text-xs text-[var(--text-muted)] py-2">No activity recorded yet.</p>
              ) : (
                <div className="space-y-0">
                  {taskAuditLog.map((entry) => (
                    <AuditLogEntry key={entry.id} entry={entry} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

// ==================== Audit Log Entry ====================

function getRelativeTime(dateStr: string): string {
  const date = new Date(dateStr + 'Z') // Treat as UTC
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return formatDate(dateStr)
}

function truncate(str: string | null, maxLen: number): string {
  if (!str) return ''
  return str.length > maxLen ? str.slice(0, maxLen) + '...' : str
}

function AuditLogEntry({ entry }: { entry: PresalesTaskAuditEntry }) {
  const icon = (() => {
    switch (entry.action) {
      case 'created':
        return <PlusCircle className="h-3.5 w-3.5 text-green-400" />
      case 'completed':
        return <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />
      case 'reopened':
        return <RotateCcw className="h-3.5 w-3.5 text-amber-400" />
      case 'edited':
        return <Edit3 className="h-3.5 w-3.5 text-blue-400" />
      case 'deleted':
        return <XCircle className="h-3.5 w-3.5 text-red-400" />
      default:
        return <Clock className="h-3.5 w-3.5 text-[var(--text-muted)]" />
    }
  })()

  const description = (() => {
    const taskDesc = truncate((entry as any).task_description || entry.new_value || entry.old_value, 40)

    switch (entry.action) {
      case 'created':
        return <span>Task <strong className="text-[var(--text-secondary)]">'{taskDesc}'</strong> created</span>
      case 'completed':
        return <span>Task <strong className="text-[var(--text-secondary)]">'{taskDesc}'</strong> marked as completed</span>
      case 'reopened':
        return <span>Task <strong className="text-[var(--text-secondary)]">'{taskDesc}'</strong> reopened</span>
      case 'deleted':
        return <span>Task <strong className="text-[var(--text-secondary)]">'{truncate(entry.old_value, 40)}'</strong> deleted</span>
      case 'edited': {
        // Status changes logged as 'edited' with field_name='status' — show as a status transition
        if (entry.field_name === 'status' && entry.old_value && entry.new_value) {
          const statusLabel = (s: string) => s === 'open' ? 'To Do' : s === 'waiting' ? 'Waiting' : s === 'handover' ? 'Handover' : s === 'completed' ? 'Done' : s
          return (
            <span>
              Task moved from <span className="text-red-300/80">{statusLabel(entry.old_value)}</span> to{' '}
              <span className="text-green-300/80">{statusLabel(entry.new_value)}</span>
            </span>
          )
        }

        const fieldLabel = entry.field_name === 'assigned_to' ? 'assignee'
          : entry.field_name === 'due_date' ? 'due date'
          : entry.field_name === 'jira_ticket' ? 'Jira ticket'
          : entry.field_name === 'key_person' ? 'key person'
          : entry.field_name === 'status_update' ? 'status update'
          : entry.field_name === 'deal' ? 'deal'
          : entry.field_name === 'prospect_company' ? 'prospect company'
          : entry.field_name || 'field'

        if (entry.old_value && entry.new_value) {
          return (
            <span>
              Task {fieldLabel} changed from <span className="text-red-300/80">'{truncate(entry.old_value, 30)}'</span> to{' '}
              <span className="text-green-300/80">'{truncate(entry.new_value, 30)}'</span>
            </span>
          )
        }
        if (entry.new_value) {
          return <span>Task {fieldLabel} set to <span className="text-green-300/80">'{truncate(entry.new_value, 30)}'</span></span>
        }
        if (entry.old_value) {
          return <span>Task {fieldLabel} <span className="text-red-300/80">'{truncate(entry.old_value, 30)}'</span> removed</span>
        }
        return <span>Task {fieldLabel} updated</span>
      }
      default:
        return <span>{entry.action}</span>
    }
  })()

  return (
    <div className="flex items-start gap-2.5 py-1.5 text-xs">
      <div className="mt-0.5 flex-shrink-0">{icon}</div>
      <div className="flex-1 min-w-0 text-[var(--text-muted)]">
        {description}
        {entry.performed_by && (
          <span className="ml-1 text-[var(--text-muted)]/60">by {entry.performed_by}</span>
        )}
      </div>
      <span className="flex-shrink-0 text-[var(--text-muted)]/50 text-[10px] mt-0.5">{getRelativeTime(entry.created_at)}</span>
    </div>
  )
}
